import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RequiredMark from '../../../shared/RequiredMark';
import '../../../shared/StyledInput.css';

// A Stage flagged "Requires commercial terms" (Opportunity and Customer by
// default) can only be entered with a Tier and at least one Customer Price
// Group — the Tier's default counts (vis_relationship_lifecycle.md §6).
// Whether this record already has both, so it can move without the prompt.
export function hasCommercialTerms(entity) {
  if (!entity?.tier_id) return false;
  return (entity.price_groups || []).length > 0 || !!entity.tier?.default_customer_price_group_id;
}

const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Shown when moving a record (Pipeline drag, Stage select) into such a
// Stage without its terms in place. Asks for the Tier; the Customer Price
// Group is asked for only when the record has none of its own, pre-filled
// from the Tier's default. onConfirm({ tier_id, customer_price_group_id })
// returns a promise — the dialog stays open on a rejection so the caller's
// error toast can explain why. Cancelling leaves the record where it was.
// title/description/confirmLabel/savingLabel override the "Move to {Stage}"
// wording for another action needing the same terms (ConvertToCustomerDialog).
function CommercialTermsPrompt({ entity, stageName, onCancel, onConfirm, title, description, confirmLabel, savingLabel }) {
  const [tiers, setTiers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tierId, setTierId] = useState(entity.tier_id ? String(entity.tier_id) : '');
  const [groupId, setGroupId] = useState(entity.tier?.default_customer_price_group_id ? String(entity.tier.default_customer_price_group_id) : '');
  const [saving, setSaving] = useState(false);
  const needsGroup = (entity.price_groups || []).length === 0;

  useEffect(() => {
    axios.get('/api/customer-tiers', { params: { status: 'active' } })
      .then((res) => setTiers(res.data.data || []))
      .catch(() => setTiers([]));
    axios.get('/api/customer-price-groups', { params: { status: 'active' } })
      .then((res) => setGroups((res.data.data || []).filter((g) => g.is_active)))
      .catch(() => setGroups([]));
  }, []);

  const tierDefaultFor = (id) => tiers.find((t) => String(t.id) === String(id))?.default_customer_price_group_id;

  // A new Tier pre-fills its default Customer Price Group, unless a group
  // has already been picked by hand.
  const pickTier = (nextTierId) => {
    const previousDefault = tierDefaultFor(tierId);
    setTierId(nextTierId);
    if (groupId === '' || String(groupId) === String(previousDefault ?? '')) {
      const nextDefault = tierDefaultFor(nextTierId);
      setGroupId(nextDefault != null ? String(nextDefault) : '');
    }
  };

  const canSave = !!tierId && (!needsGroup || !!groupId);

  const save = async () => {
    setSaving(true);
    try {
      await onConfirm({
        tier_id: Number(tierId),
        ...(needsGroup ? { customer_price_group_id: Number(groupId) } : {}),
      });
    } catch (err) {
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title={title || `Move ${entity.name} to ${stageName}`}
      description={description || `${stageName} needs commercial terms, so this record can be quoted.`}
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={save} loading={saving} disabled={!canSave}>
            {saving ? (savingLabel || 'Moving...') : (confirmLabel || `Move to ${stageName}`)}
          </SaveButton>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Tier<RequiredMark /></label><br />
          <select className="styled-input" value={tierId} onChange={(e) => pickTier(e.target.value)} style={FIELD_INPUT_STYLE}>
            <option value="" disabled>Select...</option>
            {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {needsGroup && (
          <div>
            <label style={FIELD_LABEL_STYLE}>Customer Price Group<RequiredMark /></label><br />
            <select className="styled-input" value={groupId} onChange={(e) => setGroupId(e.target.value)} style={FIELD_INPUT_STYLE}>
              <option value="" disabled>Select...</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}{String(g.id) === String(tierDefaultFor(tierId)) ? ' (Tier default)' : ''}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}

export default CommercialTermsPrompt;
